import React, { useMemo } from "react";

import { IPickerGroup, PickerItem } from "./Picker.types";
import { ResultGroup } from "./ResultGroup";

const ungrouped: IPickerGroup<"never"> = {
    id: "never",
    label: "Ungrouped",
};

function pickGroup<TTab extends string, TGroup extends string>(
    groups: IPickerGroup<TGroup>[],
    item: PickerItem<TTab, TGroup>,
): IPickerGroup<TGroup | "never"> {
    if (!item.group) {
        return ungrouped;
    }
    return groups.find(it => it.id === item.group) ?? ungrouped;
}

interface IResultListProps<TTab extends string, TGroup extends string> {
    groups: IPickerGroup<TGroup>[];
    indexedItem: PickerItem<TTab, TGroup> | null;
    setIndexedItem: (item: PickerItem<TTab, TGroup>) => void;
    onSelectItem?: (item: PickerItem<TTab, TGroup>) => void;
    children: Set<PickerItem<TTab, TGroup>>;
}

export function ResultList<TTab extends string, TGroup extends string>(
    props: IResultListProps<TTab, TGroup>,
) {
    const { children, groups, indexedItem, setIndexedItem, onSelectItem } =
        props;

    const groupedItems = useMemo(
        () =>
            [...children.values()].reduce((groupings, item) => {
                const key = pickGroup(groups, item);
                const groupList = groupings.get(key) ?? new Set();
                groupList.add(item);
                groupings.set(key, groupList);
                return groupings;
            }, new Map<IPickerGroup<TGroup | "never">, Set<PickerItem<TTab, TGroup>>>()),
        [children, groups],
    );

    const onlyUngroupedItems =
        groupedItems.size === 1 && groupedItems.has(ungrouped);

    const content = [...groupedItems.entries()].map(([group, groupItems]) => (
        <ResultGroup
            key={group.id}
            heading={group}
            hideHeading={onlyUngroupedItems}
            indexedItem={indexedItem}
            setIndexedItem={setIndexedItem}
            onSelectItem={onSelectItem}
        >
            {groupItems}
        </ResultGroup>
    ));

    return <>{content}</>;
}
